import { CSSProperties, FC } from 'react';
import useTheme from '@base/useTheme';
import { ChartSeriesType, ColorBy, TableBorderStyle, TableTileColumnVisualization } from '@common/api';
import { ChartDatumPoint, ChartRange, mapWithOutliers } from '@components/chart';
import { FlexRow, IsolateAbsolute } from '@components/layout/Flex';
import { Text } from '@components/typography/Typography';
import {
    GridChartBar,
    GridChartBreak,
    GridChartBridge,
    GridChartLabel,
    GridChartOrigin,
    GridChartPinArrowLeft,
    GridChartPinArrowRight,
    GridChartPinStick,
    GridChartPinTop,
    GridChartTrafficLights,
    GridRowCell,
    GridRowIcon,
    GridRowText,
} from './TableTile.style';

export const TABLE_CHART_SIZES: Partial<Record<ChartSeriesType, number>> = {
    [ChartSeriesType.BarChart]: 100,
    [ChartSeriesType.PinChart]: 100,
    [ChartSeriesType.WaterfallChart]: 100,
    [ChartSeriesType.IndicatorChart]: 24,
    [ChartSeriesType.TrafficLightChart]: 24,
};

export const TABLE_CELL_PADDING_X = 2;
export const TABLE_CELL_PADDING_Y = 4;

export const TABLE_SYMBOL_PADDING: Partial<Record<ChartSeriesType, number>> = {
    [ChartSeriesType.BarChart]: 2,
    [ChartSeriesType.PinChart]: 8,
    [ChartSeriesType.WaterfallChart]: 2,
};

export const TABLE_SYMBOL_SIZES: Partial<Record<ChartSeriesType, number>> = {
    [ChartSeriesType.PinChart]: 11,
};

export type TableTileChartProps = {
    point: ChartDatumPoint | null;
    range: ChartRange;
    scale?: number;

    background?: string;
    maxLabelWidth?: number;
    reserveLabelWidth?: number;
    columnVisualization: Required<TableTileColumnVisualization>;

    prevStyle: CSSProperties;
};

export const Zigzag = () => (
    <svg
        fillRule="evenodd"
        width={6}
        height={24}
        viewBox={'0 0 6 24'}
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginLeft: -3 }}
    >
        <path
            fill="none"
            strokeLinejoin="miter"
            stroke="currentColor"
            strokeWidth="2"
            d="M1 0L4 4L1 8L4 12L1 16L4 20L1 24"
        />
    </svg>
);

export const TableTileChart: FC<TableTileChartProps> = (props: TableTileChartProps) => {
    const {
        point,
        range,
        scale,
        background,
        maxLabelWidth = 0,
        reserveLabelWidth = 0,
        columnVisualization,
        prevStyle,
    } = props;

    const {
        type,
        color: { border, borderStyle, colorBy, pattern, neutral },
        header: { domainAxisBorderStyle, domainAxisColor },
    } = columnVisualization;

    const { colors } = useTheme();

    const map = (v: number) => mapWithOutliers(range, v);

    const toCSSUnits = (v: number) => (scale ? v / scale + 'px' : v * 100 + '%');
    const fixedWidth = scale ? Math.round(1 / scale) + 'px' : undefined;

    const origin = map(0);
    const rangeMin = map(range.min);
    const rangeMax = map(range.max);

    const y0 = point?.y0 || 0;
    const v = (point?.y || 0) + y0;

    const base = map(y0);
    const value = map(v);
    const prev = point?.bridge != null ? map(point.bridge) : null;

    const start = Math.min(base, value);
    const delta = Math.max(base, value) - start;
    const end = start + delta;

    const valence = point ? point.sign : 1;
    const fraction = point && point.polarity < 0 ? 1 - value : value;

    const $origin = toCSSUnits(origin);
    const $rangeMin = toCSSUnits(rangeMin);
    const $rangeMax = toCSSUnits(rangeMax);
    const $left = toCSSUnits(start);
    const $width = toCSSUnits(delta);
    const $right = toCSSUnits(end);
    const $bridge = prev != null ? toCSSUnits(prev) : null;

    const bridgeHeight = prevStyle?.height != null ? parseFloat(prevStyle.height.toString()) : 0;

    const pinHeight = TABLE_SYMBOL_SIZES[type] || 0;
    const arrowHeight = pinHeight - 2;
    const labelPadding = TABLE_SYMBOL_PADDING[type] || 0;

    const paddedLabelWidth = maxLabelWidth + labelPadding;
    const $labelLeft = `calc(${$left} - ${paddedLabelWidth}px)`;
    const $labelRight = `calc(${$right} + ${labelPadding}px)`;

    const originNode = (
        <GridChartOrigin
            $left={$origin}
            $color={domainAxisColor}
            $background={background}
            $borderStyle={domainAxisBorderStyle}
        />
    );

    const isOutlierMax = v > range.max;
    const isOutlierMin = v < range.min;
    const outlierNode =
        isOutlierMax || isOutlierMin ? (
            <GridChartBreak $left={isOutlierMax ? $rangeMax : $rangeMin} $color={background}>
                <Zigzag />
            </GridChartBreak>
        ) : null;

    const arrowNode = valence > 0 ? '▲' : '▼';

    const domainMin = range.outlierMin ?? range.min;
    const justifyChart = domainMin < 0 ? 'center' : 'start';

    const axisPadding = domainAxisBorderStyle === TableBorderStyle.Thin ? 0 : 1;
    const anomalyLeft = range.min === 0 ? reserveLabelWidth : 0;
    const anomalyRight = range.max === 0 ? reserveLabelWidth : 0;

    const spacerLeft = (
        <GridRowText $align="right" $width={domainMin < 0 ? paddedLabelWidth : axisPadding + anomalyLeft} />
    );
    const spacerRight = <GridRowText $align="left" $width={paddedLabelWidth + anomalyRight} />;

    const pointLabel = point?.label ?? <Text $transparent>–</Text>;

    const formattedIfLeft =
        valence < 0 ? (
            <GridChartLabel>
                <FlexRow $height="100%">
                    <GridRowText $align="right" $width={maxLabelWidth} $left={$labelLeft} $flexShrink={0}>
                        {pointLabel}
                    </GridRowText>
                </FlexRow>
            </GridChartLabel>
        ) : null;
    const formattedIfRight =
        valence >= 0 ? (
            <GridChartLabel>
                <FlexRow $height="100%">
                    <GridRowText $align="left" $width={maxLabelWidth} $left={$labelRight} $flexShrink={0}>
                        {pointLabel}
                    </GridRowText>
                </FlexRow>
            </GridChartLabel>
        ) : null;

    const formattedNumber = (
        <GridRowText $align="right" $width={maxLabelWidth}>
            {pointLabel}
        </GridRowText>
    );

    const dataNode =
        type === ChartSeriesType.BarChart || type === ChartSeriesType.WaterfallChart ? (
            <FlexRow $flexGrow $height="100%" $justify={justifyChart}>
                {spacerLeft}
                <FlexRow $relative $flexGrow={!scale} $width={fixedWidth}>
                    {formattedIfLeft}
                    <IsolateAbsolute>
                        {originNode}
                        {outlierNode}
                        <GridChartBar
                            $left={$left}
                            $width={$width}
                            $background={background}
                            $color={point?.color}
                            $border={colorBy === ColorBy.Static ? border : point?.color}
                            $borderStyle={borderStyle}
                            $pattern={pattern}
                        >
                            <div />
                        </GridChartBar>
                        {$bridge != null ? <GridChartBridge $left={$bridge} $bridgeHeight={bridgeHeight} /> : null}
                    </IsolateAbsolute>
                    {formattedIfRight}
                </FlexRow>
                {spacerRight}
            </FlexRow>
        ) : type === ChartSeriesType.PinChart ? (
            <FlexRow $flexGrow $height="100%" $justify={justifyChart}>
                {spacerLeft}
                <FlexRow $relative $flexGrow={!scale} $width={fixedWidth}>
                    {formattedIfLeft}
                    <IsolateAbsolute>
                        {originNode}
                        {isOutlierMax ? (
                            <>
                                <GridChartPinArrowRight $left={$right} $color={point?.color} $size={arrowHeight} />
                                <GridChartPinArrowRight
                                    $left={$right}
                                    $color={point?.color}
                                    $size={arrowHeight}
                                    $shift={-arrowHeight / 1.25}
                                />
                            </>
                        ) : isOutlierMin ? (
                            <>
                                <GridChartPinArrowLeft $left={$left} $color={point?.color} $size={arrowHeight} />
                                <GridChartPinArrowLeft
                                    $left={$left}
                                    $color={point?.color}
                                    $size={arrowHeight}
                                    $shift={arrowHeight / 1.25}
                                />
                            </>
                        ) : (
                            <GridChartPinTop
                                $left={v > 0 ? $right : $left}
                                $background={background}
                                $color={neutral}
                                $border={neutral}
                                $borderStyle={borderStyle}
                                $size={pinHeight}
                                $pattern={pattern}
                            />
                        )}
                        <GridChartPinStick $left={$left} $width={$width} $color={point?.color} />
                    </IsolateAbsolute>
                    {formattedIfRight}
                </FlexRow>
                {spacerRight}
            </FlexRow>
        ) : type === ChartSeriesType.IndicatorChart ? (
            <FlexRow $gap={4} $justify="right" $width="100%">
                {formattedNumber}
                {v !== 0 ? (
                    <GridRowIcon $inline $color={point?.color}>
                        {arrowNode}
                    </GridRowIcon>
                ) : null}
            </FlexRow>
        ) : type === ChartSeriesType.TrafficLightChart ? (
            <FlexRow $gap={4} $justify="right" $width="100%">
                {formattedNumber}
                <GridChartTrafficLights
                    style={{
                        backgroundColor:
                            fraction < 0.33
                                ? colors.error.w500
                                : fraction < 0.67
                                ? colors.warning.w500
                                : colors.success.w500,
                    }}
                />
            </FlexRow>
        ) : null;

    return <GridRowCell $flexGrow={1}>{dataNode}</GridRowCell>;
};
